import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  List as ListIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useTimeOff, useTimeOffCalendar, useTimeOffBalance } from '../../hooks/useTimeOff';
import { useHolidays } from '../../hooks/useHolidays';
import { usePendingTimeOffCount } from '../../hooks/usePendingTimeOffCount';
import NotificationBadge from '../../components/NotificationBadge';
import TimeOffCalendar from './TimeOffCalendar';
import TimeOffRequestForm from './TimeOffRequestForm';
import TimeOffRequestsList from './TimeOffRequestsList';
import LeaveBalanceCard from './LeaveBalanceCard';
import LeaveBalanceManagement from './LeaveBalanceManagement';
import HolidayManagement from './HolidayManagement/HolidayManagement';
import {
  REQUEST_STATUSES,
  STATUS_COLORS,
  TYPE_COLORS,
  TAB_INDICES,
  getTabIndex,
  TIME_OFF_ERROR_MESSAGES,
  REQUEST_TYPE_LABELS,
  ROLES,
} from '../../constants';
import { formatDate } from '../../lib/formatDate';

// Status labels for display (same as in TimeOffRequestsList)
const STATUS_LABELS = {
  [REQUEST_STATUSES.PENDING]: 'Pending',
  [REQUEST_STATUSES.APPROVED]: 'Approved',
  [REQUEST_STATUSES.DENIED]: 'Denied',
} as const;
import { TimeOffRequest } from '../../types';

// Tab name constants
const TAB_NAMES = {
  CALENDAR: 'CALENDAR' as keyof typeof TAB_INDICES,
  LEAVE_BALANCE: 'LEAVE_BALANCE' as keyof typeof TAB_INDICES,
  REQUESTS: 'REQUESTS' as keyof typeof TAB_INDICES,
  REQUEST_HISTORY: 'REQUEST_HISTORY' as keyof typeof TAB_INDICES,
  LEAVE_BALANCES: 'LEAVE_BALANCES' as keyof typeof TAB_INDICES,
  HOLIDAYS: 'HOLIDAYS' as keyof typeof TAB_INDICES,
} as const;

const TimeOff: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { pendingCount, updateCountOptimistically } = usePendingTimeOffCount();
  
  // TanStack Query hooks for data fetching
  const { 
    timeOffRequests, 
    isLoading: requestsLoading, 
    deleteTimeOff,
    updateTimeOff 
  } = useTimeOff();
  const { calendarData, isLoading: calendarLoading } = useTimeOffCalendar();
  const { balance: leaveBalance, isLoading: balanceLoading } = useTimeOffBalance();
  const { holidays, isLoading: holidaysLoading } = useHolidays();
  
  const [activeTab, setActiveTab] = useState<number>(TAB_INDICES.CALENDAR);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requestFormOpen, setRequestFormOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TimeOffRequest | null>(null);
  const [viewRequestOpen, setViewRequestOpen] = useState(false);

  const isAdmin = user?.role === ROLES.ADMIN;
  const loading = requestsLoading || calendarLoading || balanceLoading || holidaysLoading;

  const handleRequestCreated = () => {
    // Invalidate queries to refetch data
    queryClient.invalidateQueries({ queryKey: ['timeOff'] });
    queryClient.invalidateQueries({ queryKey: ['timeOffCalendar'] });
    queryClient.invalidateQueries({ queryKey: ['timeOffBalance'] });
    
    setRequestFormOpen(false);
    setSuccess('Request created successfully');
    setTimeout(() => setSuccess(''), 3000);
    
    // Update notification count optimistically for admin users
    if (isAdmin) {
      updateCountOptimistically(1);
    }
  };

  const handleRequestDeleted = async (requestId: string) => {
    try {
      deleteTimeOff(requestId);
      
      setSuccess('Request deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Delete request error:', err);
      setError(err.response?.data?.error || 'Failed to delete request');
    }
  };

  const handleViewRequest = (request: TimeOffRequest) => {
    setSelectedRequest(request);
    setViewRequestOpen(true);
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      updateTimeOff({
        id: requestId,
        data: {
          status: REQUEST_STATUSES.APPROVED,
          approvedBy: user?.id
        }
      });
      
      setSuccess('Request approved successfully');
      setTimeout(() => setSuccess(''), 3000);
      
      // Update notification count optimistically
      updateCountOptimistically(-1);
    } catch (err: any) {
      console.error('Approval error:', err);
      setError(err.response?.data?.error || TIME_OFF_ERROR_MESSAGES.APPROVE_REQUEST);
    }
  };

  const handleDenyRequest = async (requestId: string) => {
    try {
      updateTimeOff({
        id: requestId,
        data: {
          status: REQUEST_STATUSES.DENIED,
          approvedBy: user?.id
        }
      });
      
      setSuccess('Request denied successfully');
      setTimeout(() => setSuccess(''), 3000);
      
      // Update notification count optimistically
      updateCountOptimistically(-1);
    } catch (err: any) {
      setError(err.response?.data?.error || TIME_OFF_ERROR_MESSAGES.DENY_REQUEST);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Time Off Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setRequestFormOpen(true)}
        >
          New Request
        </Button>
      </Box>

      {/* Success/Error Messages */}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<CalendarIcon />}
              label="Calendar"
              iconPosition="start"
            />
            <Tab
              icon={<AccountBalanceIcon />}
              label="Leave Balance"
              iconPosition="start"
            />
            {isAdmin && (
              <Tab
                icon={
                  <NotificationBadge count={pendingCount}>
                    <PersonIcon />
                  </NotificationBadge>
                }
                label="Requests"
                iconPosition="start"
              />
            )}
            <Tab
              icon={<ListIcon />}
              label="Request history"
              iconPosition="start"
            />
            {isAdmin && (
              <Tab
                icon={<PeopleIcon />}
                label="Users"
                iconPosition="start"
              />
            )}
            {isAdmin && (
              <Tab
                icon={<SettingsIcon />}
                label="Manage Holidays"
                iconPosition="start"
              />
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Tab Content */}
      {activeTab === getTabIndex(TAB_NAMES.CALENDAR, isAdmin) && (
        <TimeOffCalendar
          data={calendarData}
          holidays={holidays}
          loading={loading}
          onViewRequest={handleViewRequest}
        />
      )}

      {activeTab === getTabIndex(TAB_NAMES.LEAVE_BALANCE, isAdmin) && leaveBalance && (
        <LeaveBalanceCard balance={leaveBalance} />
      )}

      {activeTab === getTabIndex(TAB_NAMES.REQUESTS, isAdmin) && isAdmin && (
        <TimeOffRequestsList
          requests={timeOffRequests.filter(req => req.status === REQUEST_STATUSES.PENDING)}
          loading={loading}
          onDeleteRequest={handleRequestDeleted}
          onApproveRequest={handleApproveRequest}
          onDenyRequest={handleDenyRequest}
          isAdmin={true}
          showActions={true}
          holidays={holidays}
        />
      )}

      {activeTab === getTabIndex(TAB_NAMES.REQUEST_HISTORY, isAdmin) && (
        <TimeOffRequestsList
          requests={timeOffRequests}
          loading={loading}
          onDeleteRequest={handleRequestDeleted}
          isAdmin={isAdmin}
          showYearFilter
          holidays={holidays}
        />
      )}

      {activeTab === getTabIndex(TAB_NAMES.LEAVE_BALANCES, isAdmin) && isAdmin && (
        <LeaveBalanceManagement isAdmin={isAdmin} />
      )}

      {activeTab === getTabIndex(TAB_NAMES.HOLIDAYS, isAdmin) && isAdmin && (
        <HolidayManagement 
          isAdmin={isAdmin} 
          onHolidaysChange={() => queryClient.invalidateQueries({ queryKey: ['holidays'] })}
        />
      )}

      {/* Request Form Dialog */}
      <Dialog
        open={requestFormOpen}
        onClose={() => setRequestFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Time Off Request</DialogTitle>
        <DialogContent>
          <TimeOffRequestForm
            onSubmit={handleRequestCreated}
            onCancel={() => setRequestFormOpen(false)}
            isAdmin={isAdmin}
          />
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog
        open={viewRequestOpen}
        onClose={() => setViewRequestOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* First row: Employee, Type, Status */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.employee.firstName} {selectedRequest.employee.lastName}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Type
                    </Typography>
                    <Chip
                      label={REQUEST_TYPE_LABELS[selectedRequest.type]}
                      color={TYPE_COLORS[selectedRequest.type] as any}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={STATUS_LABELS[selectedRequest.status] || selectedRequest.status}
                      color={STATUS_COLORS[selectedRequest.status] as any}
                      size="small"
                    />
                  </Box>
                </Box>

                {/* Second row: Start Date, End Date, Created */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedRequest.startDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedRequest.endDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedRequest.createdAt)}
                    </Typography>
                  </Box>
                </Box>

                {/* Third row: Reason (if exists) */}
                {selectedRequest.reason && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.reason}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TimeOff; 